import React from "react";
import PropTypes from "prop-types";

// SpendSparkline draws a glanceable trajectory of daily total spend in the Total Spend tile. It's an
// inline <svg> (no axes, grid, or tooltip) so it paints reliably in jsdom and stays dependency-free.
// It spans the tile width (preserveAspectRatio="none" + non-scaling strokes so the line stays crisp
// and the real daily volatility is NOT smoothed away), with a soft gradient fill under the actual
// line, a dashed forecast tail to month-end, and a dotted cap reference line when a cap is provided.
const WIDTH = 160;
const HEIGHT = 30;
const PAD = 2;

const SpendSparkline = ({ data, forecast = [], cap = null }) => {
  const actual = (data || []).filter((d) => d && Number.isFinite(Number(d.cost)));
  if (actual.length < 2) return null;
  const fore = (forecast || []).filter((d) => d && Number.isFinite(Number(d.cost)));

  const aVals = actual.map((d) => Number(d.cost));
  const fVals = fore.map((d) => Number(d.cost));
  const all = [...aVals, ...fVals, ...(cap ? [Number(cap)] : [])];
  const min = Math.min(...all);
  const max = Math.max(...all);
  const span = max - min || 1;

  const innerW = WIDTH - PAD * 2;
  const innerH = HEIGHT - PAD * 2;
  const total = actual.length + fore.length; // shared x-axis across both series
  const stepX = innerW / (total - 1);
  const yOf = (v) => PAD + innerH - ((v - min) / span) * innerH;
  const xOf = (i) => PAD + i * stepX;

  const aCoords = aVals.map((v, i) => `${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`);
  const lastAx = xOf(actual.length - 1);
  const lastAy = yOf(aVals[aVals.length - 1]);
  const fCoords = [`${lastAx.toFixed(1)},${lastAy.toFixed(1)}`].concat(
    fVals.map((v, i) => `${xOf(actual.length + i).toFixed(1)},${yOf(v).toFixed(1)}`)
  );

  // Soft fill under the ACTUAL line only (the forecast stays an honest dashed line, no fill).
  const areaPath =
    `M ${xOf(0).toFixed(1)} ${HEIGHT} ` +
    aVals.map((v, i) => `L ${xOf(i).toFixed(1)} ${yOf(v).toFixed(1)}`).join(" ") +
    ` L ${lastAx.toFixed(1)} ${HEIGHT} Z`;

  return (
    <svg
      width="100%"
      height={HEIGHT}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="Total spend trend with forecast"
      className="block w-full"
    >
      <defs>
        <linearGradient id="spendSparkFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity="0.22" />
          <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#spendSparkFill)" stroke="none" />
      {cap ? (
        <line
          x1={PAD}
          y1={yOf(Number(cap))}
          x2={WIDTH - PAD}
          y2={yOf(Number(cap))}
          stroke="hsl(var(--chart-grid))"
          strokeWidth="1"
          strokeDasharray="3 3"
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
      <polyline
        points={aCoords.join(" ")}
        fill="none"
        stroke="hsl(var(--chart-1))"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {fore.length ? (
        <polyline
          points={fCoords.join(" ")}
          fill="none"
          stroke="hsl(var(--chart-pred))"
          strokeWidth="1.4"
          strokeDasharray="2 2"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
    </svg>
  );
};

SpendSparkline.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      day: PropTypes.string,
      cost: PropTypes.number,
    })
  ),
  forecast: PropTypes.array,
  cap: PropTypes.number,
};

export default SpendSparkline;
